import { useState } from 'react';
import { Box, Card, IconButton, Stack, Typography, useTheme } from '@mui/material';

import { Scrollbar } from '../scrollbar';
import { StatusTag } from '../status-tag';
import { CustomAvatar, CustomAvatarGroup } from '../custom-avatar';
import { Strings } from '@app/constants';
import { Iconify } from '../iconify';
import { MemberListPopover } from './member-list-Popover';
import { useSearch } from '@app/hooks';
import { managersNames } from '@app/mock/taskBoard';

interface Props {
  projectDetails: any;
  title: string;
  heading?: string;
}
const ProjectsCard = ({ projectDetails, title, heading }: Props) => {
  const theme = useTheme();
  const titleLowerCase = title.toLowerCase().replace(/\s+/g, '');

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);

  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const addSelectedMember = (name: string) => {
    const updatedMembers = selectedMembers.includes(name)
      ? selectedMembers.filter((member: string) => member !== name)
      : [...selectedMembers, name];
    setSelectedMembers(updatedMembers);
  };

  const searchProps = useSearch({
    data: managersNames,
    fields: ['label'],
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget as any);
  };
  return (
    <Stack height="100%" p={1}>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 2,
        }}
      >
        <Stack ml={2} gap={1}>
          <Typography variant="h6">{title}</Typography>
          {selectedMembers?.length > 0 && (
            <Stack sx={{ flexDirection: 'row' }} gap={1}>
              <CustomAvatarGroup size="tiny" max={3}>
                {selectedMembers?.length > 0 &&
                  selectedMembers?.map((manager: any, index: number) => (
                    <CustomAvatar key={index} name={manager} color="info" />
                  ))}
              </CustomAvatarGroup>
            </Stack>
          )}
        </Stack>
        {titleLowerCase === 'taskassignment' && (
          <Stack
            onClick={handleClick}
            sx={{
              flexDirection: 'row',
              border: `1px solid ${theme.palette.grey[500]}`,
              alignItems: 'center',
              borderRadius: '13px',
              cursor: 'pointer',
              width: '30%',
              height: '30px',
            }}
            gap={1}
            p={1}
            mt={1}
          >
            <IconButton
              aria-label="close"
              sx={{
                color: theme.palette.primary.dark,
                p: 0,
              }}
            >
              <Iconify icon={'healthicons:ui-user-profile'} />
            </IconButton>
            <Typography sx={{ color: theme.palette.grey[500], fontSize: '13px' }}>
              {Strings.taskBoard.members}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Scrollbar sx={{ height: '90%' }}>
        <Stack>
          {projectDetails?.map((detail: any, index: any) => (
            <Card sx={{ borderRadius: 0, p: 2, height: '70px', m: 1 }} key={index}>
              <Stack
                sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
              >
                <Stack direction={'row'} gap={2}>
                  <img
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    src={`/assets/icons/card/${detail.image}.svg`}
                    style={{
                      objectFit: 'contain',
                      height: '45px',
                      alignItems: 'center',
                    }}
                  />

                  <Stack gap={0.3}>
                    <Typography
                      fontSize={{ xs: '12px', sm: '15px' }}
                      sx={{ fontWeight: 550 }}
                      fontFamily="caption"
                    >
                      {detail?.name || detail?.assetName}
                    </Typography>
                    {titleLowerCase === 'myprojects' && heading !== 'employeeDashboard' && (
                      <CustomAvatarGroup max={10} size="tiny">
                        {detail?.teamMembers?.length > 0 &&
                          detail?.teamMembers?.map((team: any, index: number) => {
                            if (team) {
                              return <CustomAvatar key={index} name={team} color="info" />;
                            }
                            return null;
                          })}
                      </CustomAvatarGroup>
                    )}
                    {(titleLowerCase === 'taskassignment' ||
                      titleLowerCase === 'remainingtasks' ||
                      titleLowerCase === 'myprojects') && (
                      <Stack sx={{ display: 'flex', flexDirection: 'row' }} gap={0.5}>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 500 }}
                        >
                          {heading === 'employeeDashboard'
                            ? `${Strings.dashboard.managedBy}:`
                            : `${Strings.dashboard.assignedBy}:`}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 600 }}
                        >
                          {heading === 'employeeDashboard' ? (
                            detail?.managers
                          ) : (
                            <Box sx={{ display: 'flex' }}>
                              <CustomAvatar
                                sx={{ mr: 0.5, fontSize: 8, height: 15, width: 15 }}
                                name={detail?.assignedBy}
                                color="info"
                              />
                              {detail?.assignedBy}
                            </Box>
                          )}
                        </Typography>
                      </Stack>
                    )}
                    {titleLowerCase === 'assets' && (
                      <Stack sx={{ display: 'flex', flexDirection: 'row' }} gap={0.5}>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 500 }}
                        >
                          {`${Strings.dashboard.assignedOn}:`}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 600 }}
                        >
                          {detail?.assignedOn}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
                <Stack
                  sx={{
                    alignContent: 'center',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '30%',
                  }}
                  gap={0.3}
                >
                  <StatusTag
                    status={titleLowerCase === 'assets' ? detail?.status : detail?.priority}
                  />
                  {(titleLowerCase === 'taskassignment' || titleLowerCase === 'remainingtasks') &&
                    heading !== 'employeeDashboard' && (
                      <Stack sx={{ display: 'flex', flexDirection: 'row' }} gap={0.5}>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 500 }}
                        >
                          {`${Strings.dashboard.dueDate}:`}
                        </Typography>
                        <Typography
                          sx={{ fontSize: '10px', color: theme.palette.grey[700], fontWeight: 600 }}
                        >
                          {detail?.dueDate}
                        </Typography>
                      </Stack>
                    )}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
        <MemberListPopover
          open={open}
          anchorEl={anchorEl}
          handleClose={handleClose}
          selectedMembers={selectedMembers}
          addSelectedMember={addSelectedMember}
          searchProps={searchProps}
        />
      </Scrollbar>
    </Stack>
  );
};

export { ProjectsCard };
